import React, { useState } from 'react';
import styles from '../../../styles/hardware-partners/hardwarePartner.module.css';
import { AdvanceAnalyticsBanner, BlackCustomButton, HardwareFeatures, HardwareTab, MarqueeComponentHomePage, Tabs, TransparentCustomButton, WhiteCustomButton } from '@/components/qbStrap';
import Image from 'next/image';
import OutletFeatures from '@/components/common/OutletFeatures';
import { ClientsArr1, testimonialData, payuFAQ, videoTestimonial, payuFeatures , getTitleContent } from '@/constants/common';
import ThreeStepForm from '@/components/common/ThreeStepForm';
import TestimonialSection from '@/components/common/TestimonialSection';
import TrustFactor from '@/components/common/TrustFactor';
import FrequentQuestions from '@/components/homePage/FrequentQuestions';
import PersonalisedDemoBanner from '@/components/common/PersonalisedDemoBanner';
import VerificationPopup from '@/components/modals/VerificationPopup';
import { useRouter } from 'next/router';
import content from '../../../language/ENGLISH.json';

const DesktopPaypal = () => {
    // ----constants----
    const paypalFeatures = [
        {
            id: 1,
            image: "/assets/payment-partners/paypal/accept-payment.webp",
            title: "Accept Payments",
            desc: "Merchants can accept PayPal transactions on QueueBuster, including credit and debit cards, offering customers flexibility in payment methods. PayPal's integration with various payment types allows businesses to cater to diverse customer preferences and increase sales opportunities.",
            tagColor: "#8AE4F6",
            bgColor: "#8ae4f626",
            imageHeight: "63rem",
            btnText: "Speak with an expert",
            points: [
                <>PayPal's dashboard enables quick and easy payments, providing businesses with access to funds for managing cash flow, and paying suppliers.</>,
                <>PayPal's platform offers global expertise and fraud monitoring to help businesses manage risk effectively, ensuring secure transactions.</>,
                <>Security is paramount with PayPal, as its platform is engineered to monitor transactions and protect businesses from threats.</>,
            ],
        },
        {
            id: 2,
            image: "/assets/payment-partners/paypal/make-payment.webp",
            title: "Make Payments",
            desc: "Access your money quickly and easily on QB with PayPal, empowering businesses to manage cash flow efficiently. Whether paying suppliers, sending funds to other PayPal users, or making online purchases, PayPal's platform ensures seamless transactions. With PayPal, businesses can conveniently access funds, enabling smooth operations and financial management.",
            tagColor: "#FFBA00",
            bgColor: "#fff7e2",
            imageHeight: "51rem",
            imageWidth: "70%",
            btnText: "Request a callback",
            points: [
                <>PayPal provides quick access to funds, allowing businesses to maintain a healthy cash flow and meet financial obligations promptly.</>,
                <>PayPal enables secure fund transfers to suppliers and other users, ensuring reliability and peace of mind.</>,
                <>Businesses can make online purchases effortlessly using funds available through PayPal, enhancing operational flexibility and convenience.</>,
            ],
        },
        {
            id: 3,
            image: "/assets/payment-partners/paypal/manage-risk.webp",
            title: "Manage Risk",
            desc: "PayPal's integration prioritizes security as a core feature, not an optional add-on. It's designed to monitor transactions actively, allowing businesses to concentrate on growth without compromising safety.",
            tagColor: "#8AE4F6",
            bgColor: "#8ae4f626",
            imageHeight: "43rem",
            imageWidth: "55rem",
            btnText: "Talk to an expert",
            points: [
                <>PayPal's platform ensures security is built-in, providing proactive monitoring of transactions to detect and prevent fraudulent activities.</>,
                <>With PayPal handling security, businesses can focus on strategic growth initiatives without worrying about transaction security.</>,
                <>PayPal's fraud monitoring continuously learns new fraud patterns, enhancing its ability to detect and mitigate potential risks in real time.</>,
            ],
        },
    ];

    const paypalFAQ = [
        {
            para: (
                <span style={{ width: "90%" }}>
                    How does PayPal integration benefit businesses using QueueBuster?
                </span>
            ),
            des: "PayPal integration with QueueBuster expands transactional capabilities, offering a unified dashboard to monitor all payments, enhancing operational efficiency.",
            id: 1,
            activeIndex: 1,
        },
        {
            para: (
                <span style={{ width: "90%" }}>
                    What payment types can merchants accept with PayPal on QueueBuster?
                </span>
            ),
            des: "Merchants can accept PayPal transactions on QueueBuster, including credit and debit cards, providing customers with flexible payment options.",
            id: 2,
            activeIndex: 0,
        },
        {
            para: (
                <span style={{ width: "90%" }}>
                    What features does PayPal's dashboard offer for businesses using QueueBuster?
                </span>
            ),
            des: "PayPal's dashboard enables quick and easy payments, and access to funds for managing cash flow and paying suppliers, simplifying financial management.",
            id: 3,
            activeIndex: 0,
        },
        {
            para: (
                <span style={{ width: "90%" }}>
                    How does PayPal help businesses manage risk effectively?
                </span>
            ),
            des: "PayPal's global expertise and fraud monitoring ensure secure transactions, minimizing risks and enhancing transaction security for businesses.",
            id: 4,
            activeIndex: 0,
        },
        {
            para: (
                <span style={{ width: "90%" }}>
                    Can businesses access funds quickly with PayPal integration on QueueBuster?
                </span>
            ),
            des: "Yes, businesses can access funds quickly and easily on QueueBuster with PayPal integration, empowering efficient cash flow management.",
            id: 5,
            activeIndex: 0,
        },

        {
            para: (
                <span style={{ width: "90%" }}>
                    What are the benefits of using PayPal for fund transfers on QueueBuster?
                </span>
            ),
            des: "PayPal enables secure fund transfers to suppliers and other users, ensuring reliability and peace of mind for businesses.",
            id: 6,
            activeIndex: 0,
        },
        {
            para: (
                <span style={{ width: "90%" }}>
                    How does PayPal's fraud monitoring enhance transaction security?
                </span>
            ),
            des: "PayPal's fraud monitoring continuously learns new fraud patterns, detecting and mitigating potential risks in real time, ensuring secure transactions.",
            id: 7,
            activeIndex: 0,
        },
        {
            para: (
                <span style={{ width: "90%" }}>
                    Does PayPal integration on QueueBuster support online purchases?
                </span>
            ),
            des: "Yes, businesses can make online purchases effortlessly using funds available through PayPal on QueueBuster, enhancing operational flexibility.",
            id: 8,
            activeIndex: 0,
        },
        {
            para: (
                <span style={{ width: "90%" }}>
                    Is security a priority with PayPal integration on QueueBuster?
                </span>
            ),
            des: "Yes, security is paramount with PayPal integration, as its platform is engineered to monitor transactions and protect businesses from threats.",
            id: 9,
            activeIndex: 0,
        },
        {
            para: (
                <span style={{ width: "90%" }}>
                    How does PayPal's integration on QueueBuster streamline payment processes?
                </span>
            ),
            des: "PayPal's integration offers one POS solution with QueueBuster for all transaction types, simplifying payment processes and enhancing overall efficiency for businesses.",
            id: 10,
            activeIndex: 0,
        },
    ]

    const [demoModal, setDemoModal] = useState(false);
    const [formHeading, setHeading] = useState("Book your free demo now!");
    const router = useRouter();

    const { commonMarqueTitle } = getTitleContent();

    return (
        <>
            <div className={styles.verificationPopupOverride}>
                <VerificationPopup isOpen={demoModal} closeModal={() => setDemoModal(false)} formHeading={formHeading} />
            </div>
            <div className={styles.hero_section}>
                <div className={styles.text_container} >
                    <div className={styles.hardware_logo} style={{ height: "5rem", width: "17rem" }}><Image src="/assets/payment-partners/paypal/PayPal.webp" alt="PayPal" fill /></div>
                    <h1 className={styles.page_heading}>
                        <span className={styles.small_black_text}>Accept Payments and Much More</span><br />
                        <span className={styles.black_text}>With</span><span className={styles.blue_text}> Complete PayPal Integration</span>
                    </h1>
                    <p className={styles.page_desc}>By choosing PayPal as your POS payment partner, you open up your store to a world of international customers. With QueueueBuster POS software, you can confidently manage your billing with secure and reliable software that is well-tested and seamlessly integrated with PayPal.</p>

                    <div className={styles.btn_container}>
                        <BlackCustomButton text="Try It Free" style={{ padding: "0 2rem" }} onClick={() => (setDemoModal(true), setHeading("14 Days free trial"))} />
                        <TransparentCustomButton text="Contact Sales" style={{ padding: "0 2rem" }} onClick={() => (setDemoModal(true), setHeading("Contact Sales"))} />
                    </div>
                </div>
                <div className={styles.hero_img} style={{ height: "52rem", width: "80rem" }}><Image src="/assets/payment-partners/paypal/hero-img.webp" alt="PayU" fill /></div>
            </div>

            <div className={styles.marquee_section}>
                <div className={styles.section_heading}><h3>{commonMarqueTitle}</h3><div className={styles.heading_stroke} style={{ width: '22rem' }}><Image src='/assets/industry/restaurant/features_hl.svg' alt='stroke' fill /></div></div>
                <MarqueeComponentHomePage ClientsArr={ClientsArr1} />
            </div>

            <div className={styles.portable_devices}>
                <h2>One POS Integration for All Transaction Types</h2>
                <HardwareFeatures features={paypalFeatures} setDemoModal={setDemoModal} setHeading={setHeading} />
            </div>
            <AdvanceAnalyticsBanner setDemoModal={setDemoModal} setHeading={setHeading} />
            <div style={{ margin: "0 9.3rem" }}><ThreeStepForm /></div>

            <div style={{ margin: "5rem 6rem" }}><TestimonialSection testimonialData={testimonialData} videoTestimonial={videoTestimonial} /></div>
            <TrustFactor />

            <FrequentQuestions FaqTableData={paypalFAQ} heading={"FAQs for QueueBuster and PayPal Integration"} />
            <div style={{ margin: "0 9.3rem" }}><PersonalisedDemoBanner setDemoModal={setDemoModal} setHeading={setHeading} /></div>
        </>
    )
}

export default DesktopPaypal